import { useCallback, useEffect } from 'react';

import { useAnalytics } from '../../contexts';
import { EnvVariable, FetchStatus } from '../../enums';
import { ICredentials } from '../../interfaces';
import { getStringEnvVariable, useDispatch, useSelector } from '../../utils';
import { useResetAccount } from '../account';
import { useResetOrganization } from '../organization';
import { useResetPatients } from '../patients';
import { useResetPhysiologicalData } from '../physiological-data';
import {
  checkEmailInDatabase,
  forgotPassword,
  login,
  logout,
  register,
  resetCheckEmailInDatabaseStatus,
  resetForgotPasswordStatus,
  resetLoginStatus,
  resetLogoutStatus,
  resetRegisterStatus,
} from './actions';
import { selectAuth } from './selectors';
import { clearAuthLocalStorage, setAuthLocalStorage } from './utils';

export const useAuth = () => useSelector(selectAuth);

export function useResetForgotPasswordStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetForgotPasswordStatus());
  }, [dispatch]);
}

export function useResetRegisterStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetRegisterStatus());
  }, [dispatch]);
}

export function useResetLoginStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetLoginStatus());
  }, [dispatch]);
}

export function useResetCheckEmailInDatabaseStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetCheckEmailInDatabaseStatus());
  }, [dispatch]);
}

export function useResetLogoutStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetLogoutStatus());
  }, [dispatch]);
}

export function useRegister() {
  const dispatch = useDispatch();

  return useCallback(
    (credentials: ICredentials) => {
      dispatch(register({ credentials }));
    },
    [dispatch],
  );
}

export function useLogin() {
  const { auth, username } = useAuth();
  const analytics = useAnalytics();
  const dispatch = useDispatch();

  useEffect(() => {
    if (auth && username) {
      analytics.identify(auth.user_name_hash, {
        email: username,
        device_language: navigator.language,
        device_type: navigator.userAgent,
        healthcare_app_version: getStringEnvVariable(EnvVariable.VERSION),
      });

      setAuthLocalStorage({
        ...auth,
        username,
      });
    }
  }, [auth, username]);

  return useCallback(
    (credentials: ICredentials) => {
      dispatch(login({ credentials }));
    },
    [dispatch],
  );
}

export function useForgotPassword() {
  const dispatch = useDispatch();

  return useCallback(
    (email: string) => {
      dispatch(forgotPassword({ email }));
    },
    [dispatch],
  );
}

export function useCheckEmailInDatabase() {
  const dispatch = useDispatch();

  return useCallback(
    (email: string) => {
      dispatch(checkEmailInDatabase({ email }));
    },
    [dispatch],
  );
}

export function useLogout() {
  const {
    status: { logout: logoutStatus },
  } = useAuth();
  const dispatch = useDispatch();
  const resetAccount = useResetAccount();
  const resetOrganization = useResetOrganization();
  const resetPatients = useResetPatients();
  const resetPhysiologicalData = useResetPhysiologicalData();

  useEffect(() => {
    if (logoutStatus === FetchStatus.SUCCESS) {
      clearAuthLocalStorage();
      resetAccount();
      resetOrganization();
      resetPatients();
      resetPhysiologicalData();
    }
  }, [logoutStatus]);

  return useCallback(() => {
    dispatch(logout());
  }, [dispatch]);
}
